import React, { useEffect, useMemo, useState } from "react";
import { BookOpen, Columns2, ExternalLink, ImagePlus, Languages, Mail, MoreHorizontal, PhoneCall, Settings, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { deriveSecondary, GenieChatInputWeb } from "./GenieChatInputWeb";
import { GenieAttachmentCard, GenieAttachmentPending } from "./GenieAttachmentCard";
import { GenieMessageMarkdown } from "./GenieMessageMarkdown";
import { GenieMist } from "./GenieMist";
import { ConversationWelcome } from "./ConversationWelcome";
import { ImageAttachmentPreviewWeb, WebImageAttachment } from "./ImageAttachmentPreviewWeb";
import { LanguagePopover } from "./LanguagePopover";
import { ChatThemePicker } from "./ChatThemePicker";
import type { GenieConversationCanvasProps } from "@/types/voice/conversation-canvas";
import { useChatTheme, ensureReadableOnWhite, readableInk } from "@/hooks/voice/useChatTheme";
import { useStickToBottom } from "@/hooks/ui/useStickToBottom";
import { useMediaLookupPending } from "@/hooks/voice/useMediaLookupPending";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/**
 * The dedicated desktop full-page conversation surface.
 *
 * The full-page text chat used to be `GenieTextChatOverlayWeb` — the embeddable
 * widget's chat panel — stretched into an 860px card floating in an empty
 * stage, which read as exactly that: a widget blown up, out of proportion on
 * any real desktop screen. This component is its own surface: the chat IS the
 * page. Full-bleed white sheet, a slim full-width identity header, a generous
 * centred conversation column, and a composer band anchored to the bottom —
 * the conversation is the sole focus, so no docked sidebars.
 *
 * Pure presentation — every prop is data or a callback, no transport. The
 * widget popup and the mobile drawer keep using `GenieTextChatOverlayWeb`;
 * nothing here affects them.
 */

const CANVAS_SETTINGS_KEY = "genie_chat_canvas_settings_v1";
const FONT_SIZE_PRESETS = [13, 15, 17, 19, 21];
const DEFAULT_FONT_INDEX = 2; // 17px — a full page earns a proper reading size.

const HG_BRAND_PRIMARY = "#4E9CFF";
const HG_BRAND_SECONDARY = "#FF4DB8";

export const GenieConversationCanvas = ({
  onRestoreSplitView,
  audioControls,
  helpPageUrl,
  onOpenDetails,
  onEmailFollowUp,
  onEmailSummary,
  emailSummaryPending = false,
  emailSummaryRequested = false,
  onCallGenie,
  onTalkToHuman,
  brandColor,
  secondaryColor,
  agentName,
  logoUrl,
  messages,
  inlineMessage,
  onChangeInlineMessage,
  onSend,
  isConnecting,
  isSpeaking,
  startedFromText,
  voiceConnected = false,
  onEndConversation,
  onEndChat,
  onCloseOverlay,
  attachments = [],
  onRemoveAttachment,
  onOpenFilePicker,
  onUpdateAttachmentAnalysis,
  imageUploadPendingCount = 0,
  imageUploadFailedCount = 0,
  imageUploadSaving = false,
  onRetryImageUploads,
  inlineCaptureSlot,
  chatStyle,
  inputBackgroundColor,
  actionButtonStyle,
  backgroundStyle,
  welcomeTextColor,
  darkSurface = false,
}: GenieConversationCanvasProps) => {
  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const { themeId, setThemeId, fontSizeIndex, setFontSizeIndex, themeColors, fontSize, lineHeight } = useChatTheme({
    storageKey: CANVAS_SETTINGS_KEY,
    brandColor: brandColor || HG_BRAND_PRIMARY,
    secondaryColor,
    defaultFontSizeIndex: DEFAULT_FONT_INDEX,
    fontSizePresets: FONT_SIZE_PRESETS,
    darkSurface,
  });

  const lastMessage = messages[messages.length - 1];
  const isWaitingForReply = !!lastMessage && lastMessage.isUser && !isConnecting && !isSpeaking;
  const mediaPending = useMediaLookupPending();

  const statusText = useMemo(() => {
    if (isConnecting) return "Connecting…";
    if (isSpeaking) return "Genie is responding…";
    if (isWaitingForReply) return "Genie is thinking…";
    return "";
  }, [isConnecting, isSpeaking, isWaitingForReply]);

  // Sending a message always returns to the latest, even after scrolling up.
  const lastMessageIsUser = lastMessage?.isUser === true;
  useEffect(() => {
    if (lastMessageIsUser) scrollToBottom();
  }, [messages.length, lastMessageIsUser, scrollToBottom]);

  const brand = brandColor || HG_BRAND_PRIMARY;
  const genieAccent = useMemo(
    () =>
      secondaryColor ||
      (brand !== HG_BRAND_PRIMARY ? deriveSecondary(brand) : HG_BRAND_SECONDARY),
    [brand, secondaryColor],
  );
  // Message labels/links sit on the chat background; the status line sits in
  // the always-white header.
  const userInk = useMemo(() => readableInk(brand, darkSurface), [brand, darkSurface]);
  const genieInk = useMemo(() => readableInk(genieAccent, darkSurface), [genieAccent, darkSurface]);

  const headerActive = statusText.length > 0;
  const statusInk = headerActive ? ensureReadableOnWhite(brand) : "#64748B";

  const genieAvatarChip = (
    <span
      className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl"
      style={{ background: `linear-gradient(135deg, ${brand}1a, ${genieAccent}1a)` }}
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-6 w-6 rounded-md object-contain" />
      ) : (
        <GenieIcon
          name="genie-lamp"
          size={16}
          color={brand}
          secondaryColor={genieAccent}
          gradientEnabled
        />
      )}
    </span>
  );

  return (
    <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white" style={backgroundStyle}>
      {/* One-time brand mist bloom so the page still feels summoned, not loaded. */}
      <GenieMist brandColor={brand} secondaryColor={genieAccent} intensity={0.45} />

      {/* ===== Identity header — full width, slim ===== */}
      <div className="relative z-20 flex shrink-0 items-center gap-3 border-b border-[#F1F4F9] bg-white/95 px-5 py-3 sm:px-8">
        <span
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl"
          style={{ background: `linear-gradient(135deg, ${brand}1a, ${genieAccent}1a)` }}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-8 w-8 rounded-lg object-contain" />
          ) : (
            <GenieIcon
              name="genie-lamp"
              size={20}
              color={brand}
              secondaryColor={genieAccent}
              gradientEnabled
            />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold leading-tight text-[#222B45]">
            {agentName || "Genie"}
          </p>
          <span
            className="mt-0.5 flex items-center gap-1.5 text-[12px] leading-tight"
            style={{ color: statusInk }}
          >
            <span className="relative flex h-1.5 w-1.5">
              {headerActive && (
                <span
                  className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70"
                  style={{ backgroundColor: brand }}
                />
              )}
              <span
                className="relative inline-flex h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: brand }}
              />
            </span>
            {statusText || "Online"}
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 self-center">
          <LanguagePopover
            brandColor={brand}
            variant="modal"
            hideTrigger
            open={showLanguageModal}
            onOpenChange={setShowLanguageModal}
          />
          {onRestoreSplitView && (
            <Button
              type="button"
              variant="ghost"
              onClick={onRestoreSplitView}
               className="h-11 w-11 rounded-full p-0 text-gray-600"
               title="Back to split view"
               aria-label="Back to split view"
            >
              <Columns2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}
          {audioControls}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" className="h-11 w-11 rounded-full p-0 text-gray-600" title="Chat settings" aria-label="Chat settings">
                <Settings className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[3100] w-64 space-y-3 rounded-xl border-genie-border bg-white p-3 text-gray-700">
              <div>
                <DropdownMenuLabel className="px-0 pt-0">Chat text size</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={String(fontSizeIndex)}
                  onValueChange={(value) => setFontSizeIndex(Number(value))}
                  className="flex items-center justify-between gap-1"
                >
                  {FONT_SIZE_PRESETS.map((size, index) => (
                    <DropdownMenuRadioItem
                      key={size}
                      value={String(index)}
                      className="flex-1 justify-center rounded-lg py-2 text-center [&>span:first-child]:hidden"
                    >
                      {size}px
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </div>
              <ChatThemePicker themeId={themeId} onChange={setThemeId} labelClassName="text-sm font-semibold" />
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" className="h-11 w-11 rounded-full p-0 text-gray-600" title="More" aria-label="More">
                <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            {/* Portalled menus must sit above the support-page stage (z-3000). */}
            <DropdownMenuContent align="end" className="z-[3100] w-56 rounded-xl border-genie-border bg-white p-1.5 text-gray-700">
              {onEmailSummary && (
                <DropdownMenuItem onSelect={onEmailSummary} disabled={emailSummaryPending} className="min-h-[44px] gap-2 rounded-lg">
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {emailSummaryRequested ? "Summary requested" : "Email summary"}
                </DropdownMenuItem>
              )}
              {onEmailFollowUp && (
                <DropdownMenuItem onSelect={onEmailFollowUp} className="min-h-[44px] gap-2 rounded-lg">
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  Continue over email
                </DropdownMenuItem>
              )}
              {onCallGenie && (
                <DropdownMenuItem onSelect={onCallGenie} className="min-h-[44px] gap-2 rounded-lg">
                  <PhoneCall className="h-4 w-4" aria-hidden="true" />
                  Call the Genie
                </DropdownMenuItem>
              )}
              {onTalkToHuman && (
                <DropdownMenuItem onSelect={onTalkToHuman} className="min-h-[44px] gap-2 rounded-lg">
                  <PhoneCall className="h-4 w-4" aria-hidden="true" />
                  Talk to human
                </DropdownMenuItem>
              )}
              {onOpenFilePicker && (
                <DropdownMenuItem onSelect={onOpenFilePicker} className="min-h-[44px] gap-2 rounded-lg">
                  <ImagePlus className="h-4 w-4" aria-hidden="true" />
                  Add a photo
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => setShowLanguageModal(true)} className="min-h-[44px] gap-2 rounded-lg">
                <Languages className="h-4 w-4" aria-hidden="true" />
                Languages
              </DropdownMenuItem>
              {onOpenDetails && (
                <DropdownMenuItem onSelect={onOpenDetails} className="min-h-[44px] gap-2 rounded-lg">
                  <BookOpen className="h-4 w-4" aria-hidden="true" />
                  Links & resources
                </DropdownMenuItem>
              )}
              {helpPageUrl && (
                <DropdownMenuItem asChild className="min-h-[44px] gap-2 rounded-lg">
                  <a href={helpPageUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    Support page
                  </a>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {voiceConnected && onEndConversation ? (
            <button
              type="button"
              onClick={onEndConversation}
              className="ml-1 rounded-full bg-red-50 px-3.5 py-2 text-xs font-semibold text-red-600 hover:bg-red-100"
              title="End conversation"
            >
              End
            </button>
          ) : startedFromText ? (
            <button
              type="button"
              onClick={onEndChat}
              className="ml-1 rounded-full bg-red-50 px-3.5 py-2 text-xs font-semibold text-red-600 hover:bg-red-100"
            >
              End chat
            </button>
          ) : (
            <button
              type="button"
              onClick={onCloseOverlay}
              className="flex h-9 w-9 items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:bg-black/[0.04] hover:text-[#222B45]"
              title="Close chat"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

      </div>

      {/* ===== Body: the conversation column, full focus ===== */}
      <div className="relative z-10 flex min-h-0 flex-1">
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {messages.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto before:flex-1 after:flex-1">
              <ConversationWelcome brandColor={brand} secondaryColor={genieAccent} agentName={agentName} logoUrl={logoUrl} chatStyle={chatStyle} textColor={welcomeTextColor} darkSurface={darkSurface} />
            </div>
          ) : (
            <div
              ref={scrollRef}
              className="brand-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain"
            >
              <div ref={contentRef} className="mx-auto w-full max-w-[860px] space-y-6 px-5 pb-8 pt-8 sm:px-8">
                {messages.map((msg, idx) => {
                  const key = `${msg.timestamp.getTime()}-${idx}`;

                  if (Array.isArray(msg.imageAttachments) && msg.imageAttachments.length > 0) {
                    const imageAttachments: WebImageAttachment[] = msg.imageAttachments.map(
                      (img, index: number) => ({
                        id: img.id || `${idx}-img-${index}`,
                        previewUrl: img.previewUrl,
                        status: "ready",
                        analysisText: img.analysisText,
                      }),
                    );
                    return (
                      <div key={key} className="flex justify-end">
                        <ImageAttachmentPreviewWeb
                          attachments={imageAttachments}
                          onRemove={() => undefined}
                          isPreviewOnly={true}
                        />
                      </div>
                    );
                  }

                  const prev = messages[idx - 1];
                  const prevIsSameAuthor =
                    !!prev &&
                    prev.isUser === msg.isUser &&
                    !(Array.isArray(prev.imageAttachments) && prev.imageAttachments.length > 0);
                  const timestamp = msg.timestamp.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  });

                  if (msg.isUser) {
                    return (
                      <div key={key} className={cn("group flex flex-col items-end", prevIsSameAuthor && "-mt-3")}>
                        {!prevIsSameAuthor && (
                          <div className="mb-1 flex flex-row-reverse items-baseline gap-1.5 pr-1">
                            <span className="text-[11px] font-medium" style={{ color: userInk }}>
                              You
                            </span>
                            <span className="text-[11px] text-[#94A3B8] opacity-0 transition-opacity group-hover:opacity-100">
                              {timestamp}
                            </span>
                          </div>
                        )}
                        <div
                          className={cn(
                            "max-w-[70%] whitespace-pre-wrap text-left font-sans",
                            themeColors.style === "tinted"
                              ? "rounded-2xl rounded-tr-md px-4 py-2.5"
                              : themeColors.style === "edged"
                                ? "border-r-2 py-1 pr-3"
                                : "rounded-2xl rounded-tr-md px-4 py-2.5",
                          )}
                          style={{
                            fontSize,
                            lineHeight: `${lineHeight}px`,
                            letterSpacing: "-0.006em",
                            color: themeColors.userTextColor,
                            backgroundColor:
                              themeColors.style === "edged" ? "transparent" : themeColors.userBubbleBg,
                            borderColor: themeColors.style === "edged" ? themeColors.userRuleColor : undefined,
                          }}
                        >
                          {msg.message}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className={cn("group flex items-start gap-3", prevIsSameAuthor && "-mt-3")}>
                      {!prevIsSameAuthor ? genieAvatarChip : <span className="w-8 flex-shrink-0" aria-hidden />}
                      <div className="min-w-0 flex-1">
                        {!prevIsSameAuthor && (
                          <div className="mb-1 flex items-baseline gap-1.5">
                            <span className="text-[11px] font-medium" style={{ color: genieInk }}>
                              {agentName || "Genie"}
                            </span>
                            <span className="text-[11px] text-[#94A3B8] opacity-0 transition-opacity group-hover:opacity-100">
                              {timestamp}
                            </span>
                          </div>
                        )}
                        {msg.attachment ? (
                          <GenieAttachmentCard attachment={msg.attachment} brandColor={genieInk} />
                        ) : (
                          <div
                            className={cn(
                              themeColors.style === "tinted" && "rounded-2xl px-4 py-3",
                              themeColors.style === "edged" && "border-l-2 py-1 pl-3",
                            )}
                            style={{
                              backgroundColor: themeColors.style === "tinted" ? themeColors.genieBubbleBg : undefined,
                              borderColor: themeColors.style === "edged" ? themeColors.genieRuleColor : undefined,
                            }}
                          >
                            <GenieMessageMarkdown
                              text={msg.message}
                              fontSize={fontSize}
                              lineHeight={lineHeight}
                              color={themeColors.genieTextColor}
                              accentColor={genieInk}
                              measure="max-w-[68ch]"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {mediaPending && (
                  <div className="pl-11">
                    <GenieAttachmentPending brandColor={genieInk} />
                  </div>
                )}

                {inlineCaptureSlot && <div className="pl-11">{inlineCaptureSlot}</div>}

                {/* Live "thinking" indicator — the reply forms in place instead of
                    a status line hiding by the composer. */}
                {statusText && (
                  <div className="flex items-start gap-3">
                    {genieAvatarChip}
                    <div className="flex h-8 items-center gap-1.5">
                      {[0, 1, 2].map((dot) => (
                        <span
                          key={dot}
                          className="h-2 w-2 animate-bounce rounded-full"
                          style={{
                            backgroundColor: `${genieAccent}99`,
                            animationDelay: `${dot * 150}ms`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content fades out above the composer instead of clipping mid-line. */}
          {messages.length > 0 && !backgroundStyle && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>
      </div>

      {/* ===== Composer band ===== */}
      <div className="relative z-20 shrink-0 border-t border-[#F1F4F9] bg-white px-5 pb-5 pt-4 sm:px-8">
        <div className="mx-auto w-full max-w-[860px] space-y-2">
          {attachments.length > 0 && onRemoveAttachment && (
            <ImageAttachmentPreviewWeb
              attachments={attachments}
              onRemove={onRemoveAttachment}
              onUpdateAnalysis={onUpdateAttachmentAnalysis}
            />
          )}
          {imageUploadPendingCount > 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-[#E5EAF0] bg-white px-3 py-2 text-xs text-[#64748B]">
              <span className="min-w-0 flex-1">
                {imageUploadSaving
                  ? `Saving ${imageUploadPendingCount} photo${imageUploadPendingCount === 1 ? "" : "s"}...`
                  : `${imageUploadFailedCount} photo upload${imageUploadFailedCount === 1 ? "" : "s"} failed`}
              </span>
              {!imageUploadSaving && imageUploadFailedCount > 0 && onRetryImageUploads && (
                <button
                  type="button"
                  onClick={onRetryImageUploads}
                  className="font-semibold text-genie-primary hover:underline"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          <GenieChatInputWeb
            value={inlineMessage}
            onChange={onChangeInlineMessage}
            onSend={onSend}
            disabled={isConnecting || attachments.some((attachment) => attachment.status === "analyzing")}
            hasAttachments={attachments.length > 0}
            onRemoveAttachment={onRemoveAttachment}
            onOpenFilePicker={onOpenFilePicker}
            brandColor={brand}
            secondaryColor={secondaryColor}
            backgroundColor={inputBackgroundColor}
            chatStyle={chatStyle}
            actionButtonStyle={actionButtonStyle}
          />
        </div>
      </div>
    </div>
  );
};

export default GenieConversationCanvas;
